import {
  ComponentGraphGroup,
  ComponentGraphNode,
  GraphItem,
  WORKSPACE_ICON_REPRESENTATION,
} from '@ardoq/graph';
import { RepresentationData } from '@ardoq/data-model';
import { ArdoqId, GroupType } from '@ardoq/api-types';
import { canvasResolveImage } from 'tabview/canvasRendering/canvasResolvedImages';
import type { ComponentRepresentationData } from '@ardoq/basic-components';
import type {
  GraphNodesToRelationshipsNodesReducerState,
  RelationshipsViewGraphNode,
} from 'tabview/relationshipDiagrams/types';
import { createNode, isGroup } from './createNode';
import { RelationshipsNode } from '../types';

const isComponent = (graphNode: GraphItem): graphNode is ComponentGraphNode =>
  graphNode.isComponent();

const getRepresentationData = (
  graphNode: RelationshipsViewGraphNode,
  componentRepresentationDataByComponentId: Map<
    ArdoqId,
    ComponentRepresentationData | null
  >,
  children?: RelationshipsViewGraphNode[]
): RepresentationData | null => {
  if (isComponent(graphNode)) {
    return graphNode.getRepresentationData();
  }
  if (isGroup(graphNode)) {
    return graphNode.type === GroupType.WORKSPACE
      ? WORKSPACE_ICON_REPRESENTATION
      : graphNode.type === GroupType.COMPONENT
        ? // this is a bit ugly to use the first child to get the component type info,
          // but we don't have the actual identifier of the workspace model at this point.
          (componentRepresentationDataByComponentId.get(
            children?.[0]?.modelId ?? ''
          ) ?? null)
        : null;
  }
  return null;
};

const reduceNodes = (
  state: GraphNodesToRelationshipsNodesReducerState<RelationshipsNode>,
  graphNode: RelationshipsViewGraphNode
) => {
  const children = state.groupChildren.get(graphNode as ComponentGraphGroup);

  const { parent } = state;

  const representationData: RepresentationData | null = getRepresentationData(
    graphNode,
    state.componentRepresentationDataByComponentId,
    children
  );
  if (representationData?.isImage && representationData?.value) {
    canvasResolveImage(representationData);
  }

  const node = createNode({
    graphNode,
    parent,
    children,
    representationData,
    colorFilterIdsByComponentId: state.colorFilterIdsByComponentId,
    contextComponentId: state.contextComponentId,
    typeModelIdByComponentId: state.typeModelIdByComponentId,
  });
  if (children) {
    children.reduce(reduceNodes, {
      ...state,
      parent: node,
    });
  }
  if (state.parent) {
    state.parent.children ??= [];
    state.parent.children.push(node);
  }
  state.map.set(graphNode.id, node);
  return state;
};

export { reduceNodes };
